import Util from "./util";

const CacheKey = "AllCacheKey";

interface ExpireObj {
  expireSeconds: number;
  setDate: Date;
}

interface CacheKeyMap {
  [key: string]: ExpireObj;
}

class StorageHelper {
  private _getAllCacheKeyArray: CacheKeyMap = {};
  constructor() {
    // 绑定方法上下文
    this.set = this.set.bind(this);
    this.get = this.get.bind(this);
    this.clear = this.clear.bind(this);
    this.remove = this.remove.bind(this);
    this.getStorageInfo = this.getStorageInfo.bind(this);
  }

  /**
   * 设置缓存数据
   * @param key 存储键名
   * @param data 要存储的数据
   * @param expireSeconds 过期时间（秒），0表示永不过期
   */
  set(key: string, data: any, expireSeconds: number = 0): void {
    if (Util.isEmpty(key)) {
      throw new Error("[set]key参数不能为空！");
    }

    const expireObj: ExpireObj = {
      expireSeconds: expireSeconds || 0,
      setDate: new Date(),
    };

    this.updateExpireCacheKey(key, expireObj);
    localStorage.setItem(key, JSON.stringify(data));
  }

  /**
   * 获取缓存数据
   * @param key 存储键名
   * @returns 存储的数据或null（如果不存在或已过期）
   */
  get<T = any>(key: string): T | null {
    if (Util.isEmpty(key)) {
      throw new Error("[get]key参数不能为空！");
    }

    const expireObj = this.getExpireObj(key);

    // 无过期配置或永不过期
    if (
      Util.isEmpty(expireObj) ||
      Util.isEmpty(expireObj.expireSeconds) ||
      expireObj.expireSeconds < 1
    ) {
      const item = localStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    }

    // 检查是否过期
    const difMileSeconds = Math.abs(Date.now() - new Date(expireObj.setDate).getTime());
    if (difMileSeconds > expireObj.expireSeconds * 1000) {
      this.remove(key); // 自动移除过期缓存
      return null;
    }

    const item = localStorage.getItem(key);
    return item ? JSON.parse(item) : null;
  }

  /**
   * 移除指定缓存
   * @param key 要移除的键名
   */
  remove(key: string): void {
    if (Util.isEmpty(key)) {
      throw new Error("[remove]key参数不能为空！");
    }

    this.removeExpireCacheKey(key);
    localStorage.removeItem(key);
    console.log(`[remove]${key}成功！`);
  }

  /**
   * 清除所有缓存
   */
  clear(): void {
    const allCacheKey = this.getAllCacheKeyArray();
    Object.keys(allCacheKey).forEach(key => {
      localStorage.removeItem(key);
    });
    localStorage.removeItem(CacheKey);
    this._getAllCacheKeyArray = {};
  }

  /**
   * 更新缓存的过期时间记录
   * @param key 键名
   * @param expireObj 过期配置对象
   */
  private updateExpireCacheKey(key: string, expireObj: ExpireObj): void {
    const allCacheKey = this.getAllCacheKeyArray();
    allCacheKey[key] = expireObj;
    localStorage.setItem(CacheKey, JSON.stringify(allCacheKey));
  }

  /**
   * 移除缓存的过期时间记录
   * @param key 键名
   */
  private removeExpireCacheKey(key: string): void {
    const allCacheKey = this.getAllCacheKeyArray();
    if (!Util.isEmpty(allCacheKey)) {
      delete allCacheKey[key];
      localStorage.setItem(CacheKey, JSON.stringify(allCacheKey));
    }
  }

  /**
   * 获取缓存的过期配置
   * @param key 键名
   * @returns 过期配置对象或null
   */
  private getExpireObj(key: string): ExpireObj | null {
    const allCacheKey = this.getAllCacheKeyArray();
    return allCacheKey[key] || null;
  }

  /**
   * 获取所有缓存键的过期记录
   * @returns 缓存键映射对象
   */
  private getAllCacheKeyArray(): CacheKeyMap {
    if (Util.isEmpty(this._getAllCacheKeyArray)) {
      const cacheKeys = localStorage.getItem(CacheKey);
      this._getAllCacheKeyArray = cacheKeys ? JSON.parse(cacheKeys) : {};
    }
    return this._getAllCacheKeyArray;
  }

  /**
   * 获取存储信息
   * @returns 包含keys、currentSize和limitSize的对象
   */
  getStorageInfo(): {
    keys: string[];
    currentSize: number;
    limitSize: number;
  } {
    const keys = Object.keys(localStorage);
    const values = keys.map(key => localStorage.getItem(key) || "");
    return {
      keys,
      currentSize: new Blob(values).size,
      limitSize: 5 * 1024 * 1024, // 5MB
    };
  }
}

const Storage = new StorageHelper();

export const { set, get, clear, remove, getStorageInfo } = Storage;
export const S = Storage;
export default S;
